import { MKLog } from "./MKLog";
import { MKUtils } from "./MKUtils";
import { MKAzukiRequest } from "../request";
import { MKError, MKProgramInfo } from "../../interfaces";


/**
 * Live program manager class.
 *
 * Maintains a list of programs within the current live source and provides utility methods.
 *
 * @internal
 */
export class MKProgramManager {
    private azukiRequest?: MKAzukiRequest;
    private programInfoList: MKProgramInfo[];
    private currentProgramInfo?: MKProgramInfo;

    /**
     * Class constructor.
     * @param azukiRequest handle to azuki request instance
     */
    constructor(azukiRequest?: MKAzukiRequest) {
        this.programInfoList = [];
        this.azukiRequest = azukiRequest;
        if (!this.azukiRequest) {
            MKLog.error("[MKProgramManager] invalid azuki request handle!");
        }
    }

    /**
     * Reset program info list.
     */
    reset() {
        this.programInfoList = [];
    }

    /**
     * Using the current program info fetch and update the program info list with previous, current and next program info.
     * @param currentProgramInfo current live program info object.
     */
    updateProgramInfoList(currentProgramInfo: MKProgramInfo): void {
        if (currentProgramInfo.timeRange && this.azukiRequest) {
            // update current program info
            this.currentProgramInfo = currentProgramInfo;

            // To fetch previous, current and next program information we need to set the time range
            // for the query request such that `startDate` lies in the boundary of the previous program
            // and `endDate` lies in the boundary for the next program. This way we will possibly get
            // previous, current and next program information if available.
            const startDate = MKUtils.timestampToISOString((currentProgramInfo.timeRange.start - 60) * 1000);
            const endDate = MKUtils.timestampToISOString((currentProgramInfo.timeRange.end + 60) * 1000);
            this.azukiRequest.programInfoRequest(startDate, endDate)
            .then((programInfoList: MKProgramInfo[]) => {
                if (programInfoList.length > 0) {
                    this.programInfoList = programInfoList;
                } else {
                    // keep at least the current program in the list
                    this.programInfoList = [];
                    this.programInfoList.push(currentProgramInfo);
                }
            });
        }
    }

    /**
     * Validate and return control (non-playback) blocking restrictions.
     *
     * If any playback blocking restrictions are applicable then an error will be thrown.
     *
     * @param programInfo object containing program restrictions to validate.
     * @returns list of control (non-playback) blocking restrictions or an empty list.
     */
    validateProgramRestrictions(programInfo: MKProgramInfo): MKError[] {
        if (programInfo.programRestrictions.playbackBlocking.length > 0) {
            // throw the first available playback blocking restriction error
            throw programInfo.programRestrictions.playbackBlocking[0];
        }
        return programInfo.programRestrictions.controlBlocking;
    }

    /**
     * Check for program boundary based on the current player position.
     *
     *
     *        prev program             current program              next program
     * [-----------------------------|-----------------------------|-----------------------------]
     *
     * ```
     * if (currentTime < currentProgram.startTime || currentTime > currentProgram.endTime) {
     *     // program boundary crossed.
     * }
     * ```
     * @param currentTime the current player time.
     * @returns current program info after boundary cross or `null` if program info not available.
     */
    checkProgramBoundary(currentTime: number): MKProgramInfo | null {
        if (this.currentProgramInfo?.timeRange) {
            if (currentTime < this.currentProgramInfo.timeRange.start || currentTime > this.currentProgramInfo.timeRange.end) {
                // program boundary cross
                MKLog.debug("[MKProgramManager] program boundary crossed.");
                const programInfo = this.getProgramInfo(currentTime);
                if (programInfo) {
                    this.updateProgramInfoList(programInfo);
                    return programInfo;
                } else {
                    // reset as current program info is not available
                    this.currentProgramInfo = undefined;
                    MKLog.warn("[MKProgramManager] next/previous program info not available.");
                }
            }
        }
        return null;
    }

    /**
     * Get program info for the current time.
     * @param currentTime current playback time.
     * @returns current program info if available, `null` otherwise.
     */
    private getProgramInfo(currentTime: number): MKProgramInfo | null {
        const index = this.programInfoList.findIndex((programInfo: MKProgramInfo) => {
            if (programInfo.timeRange) {
                return currentTime >= programInfo.timeRange.start && currentTime < programInfo.timeRange.end;
            }
            return -1;
        });

        if (index >= 0 && index < this.programInfoList.length) {
            return this.programInfoList[index];
        }

        return null;
    }
}